import React, { useState, useEffect } from 'react';
import { useWorkspace } from '../../../context/WorkspaceContext';
import { Card } from '../../../design-system/Card';
import { Button } from '../../../design-system/Button';
import { Badge } from '../../../design-system/Badge';
import { Modal } from '../../../design-system/Modal';
import { Input } from '../../../design-system/Input';
import { Select } from '../../../design-system/Dropdown';
import {
  Zap,
  Plus,
  Play,
  CheckCircle2,
  Sparkles,
  History,
  Trash2
} from 'lucide-react';

export const AutomationsView = () => {
  const { activeProject } = useWorkspace();
  const storageKey = `forma_automations_${activeProject?.id || 'default'}`;

  const [activeTab, setActiveTab] = useState('rules'); // 'rules' or 'logs'
  const [isBuilderModalOpen, setIsBuilderModalOpen] = useState(false);

  // Builder Form State
  const [ruleName, setRuleName] = useState('');
  const [selectedTrigger, setSelectedTrigger] = useState('task_overdue');
  const [selectedCondition, setSelectedCondition] = useState('priority_not_urgent');
  const [selectedAction, setSelectedAction] = useState('set_urgent_notify');

  const [rules, setRules] = useState(() => {
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      } catch (e) {}
    }
    return [
      {
        id: 'auto_1',
        name: 'Escalate Overdue Tasks to Urgent',
        trigger: 'When a task passes its due date',
        condition: 'If priority is not already Urgent',
        action: 'Set priority to Urgent & send push notification to assignee',
        enabled: true,
        executionCount: 14,
        lastRun: 'Today at 08:00 AM'
      },
      {
        id: 'auto_2',
        name: 'Unblock Dependent Tasks on Done',
        trigger: 'When a task status changes to Done',
        condition: 'If task is blocking prerequisite tasks',
        action: 'Clear blocked status on dependent tasks and alert watchers',
        enabled: true,
        executionCount: 28,
        lastRun: 'Today at 09:15 AM'
      },
      {
        id: 'auto_3',
        name: 'Auto-Assign Today Queue for New Assignments',
        trigger: 'When task assignee is changed',
        condition: 'Always',
        action: 'Add task to assignee personal "Today" queue & send notification',
        enabled: true,
        executionCount: 42,
        lastRun: 'Yesterday'
      }
    ];
  });

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(rules));
  }, [rules, storageKey]);

  const [logs, setLogs] = useState([
    {
      id: 'log_1',
      ruleName: 'Unblock Dependent Tasks on Done',
      trigger: 'Task moved to Done',
      result: 'Success',
      time: '1 hour ago',
      details: 'Unblocked dependent tasks and sent live notification to workspace'
    },
    {
      id: 'log_2',
      ruleName: 'Escalate Overdue Tasks to Urgent',
      trigger: 'Cron evaluation at 08:00 AM',
      result: 'Success',
      time: 'Today at 08:00 AM',
      details: 'Evaluated project tasks. 0 overdue tasks found.'
    }
  ]);

  const triggerOptions = [
    { value: 'task_overdue', label: 'When a task passes its due date' },
    { value: 'task_completed', label: 'When a task status changes to Done' },
    { value: 'task_assigned', label: 'When a task is assigned to a teammate' },
    { value: 'sprint_started', label: 'When a new Sprint starts' },
    { value: 'priority_urgent', label: 'When task priority is set to Urgent' }
  ];

  const conditionOptions = [
    { value: 'none', label: 'Always (No condition filter)' },
    { value: 'priority_not_urgent', label: 'If priority is not already Urgent' },
    { value: 'has_dependencies', label: 'If task has dependent blockers' },
    { value: 'estimate_large', label: 'If estimate is greater than 5 pts' }
  ];

  const actionOptions = [
    { value: 'set_urgent_notify', label: 'Set priority to Urgent & notify assignee' },
    { value: 'unblock_dependents', label: 'Unblock dependent tasks & notify watchers' },
    { value: 'add_to_today', label: 'Add to assignee personal Today queue' },
    { value: 'post_comment', label: 'Post automatic comment in discussion stream' }
  ];

  const handleToggleRule = (ruleId) => {
    setRules((prev) =>
      prev.map((r) => (r.id === ruleId ? { ...r, enabled: !r.enabled } : r))
    );
  };

  const handleDeleteRule = (ruleId) => {
    if (window.confirm('Delete this automation rule?')) {
      setRules((prev) => prev.filter((r) => r.id !== ruleId));
    }
  };

  const handleTestRunRule = (rule) => {
    const newLog = {
      id: 'log_' + Date.now(),
      ruleName: rule.name,
      trigger: 'Manual Test Execution',
      result: 'Success',
      time: 'Just now',
      details: `Trigger "${rule.trigger}" executed successfully with action "${rule.action}".`
    };

    setLogs([newLog, ...logs]);
    setRules((prev) =>
      prev.map((r) =>
        r.id === rule.id
          ? { ...r, executionCount: r.executionCount + 1, lastRun: 'Just now' }
          : r
      )
    );
  };

  const handleCreateRuleSubmit = (e) => {
    e?.preventDefault();
    if (!ruleName.trim()) return;

    const triggerLabel = triggerOptions.find((t) => t.value === selectedTrigger)?.label || selectedTrigger;
    const conditionLabel = conditionOptions.find((c) => c.value === selectedCondition)?.label || selectedCondition;
    const actionLabel = actionOptions.find((a) => a.value === selectedAction)?.label || selectedAction;

    const newR = {
      id: 'auto_' + Date.now(),
      name: ruleName.trim(),
      trigger: triggerLabel,
      condition: conditionLabel,
      action: actionLabel,
      enabled: true,
      executionCount: 0,
      lastRun: 'Never'
    };

    setRules([newR, ...rules]);
    setRuleName('');
    setIsBuilderModalOpen(false);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }} className="animate-fade-in">
      {/* Header Toolbar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <Badge variant="teal" size="sm">No-Code Engine</Badge>
            <h2 style={{ fontSize: '20px', fontWeight: '800' }}>Visual Automations & Event Triggers</h2>
          </div>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
            Automate recurring project workflows, escalation rules, and dependency transitions without code
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ display: 'flex', backgroundColor: 'var(--surface-recessed)', borderRadius: 'var(--radius-sm)', padding: '2px' }}>
            <button
              onClick={() => setActiveTab('rules')}
              style={{
                padding: '6px 12px',
                borderRadius: 'var(--radius-sm)',
                backgroundColor: activeTab === 'rules' ? 'var(--surface-card)' : 'transparent',
                boxShadow: activeTab === 'rules' ? 'var(--shadow-raised-sm)' : 'none',
                border: 'none',
                color: activeTab === 'rules' ? 'var(--accent-teal)' : 'var(--text-muted)',
                fontSize: '12px',
                fontWeight: '700',
                cursor: 'pointer'
              }}
            >
              Active Rules ({rules.filter((r) => r.enabled).length})
            </button>
            <button
              onClick={() => setActiveTab('logs')}
              style={{
                padding: '6px 12px',
                borderRadius: 'var(--radius-sm)',
                backgroundColor: activeTab === 'logs' ? 'var(--surface-card)' : 'transparent',
                boxShadow: activeTab === 'logs' ? 'var(--shadow-raised-sm)' : 'none',
                border: 'none',
                color: activeTab === 'logs' ? 'var(--accent-teal)' : 'var(--text-muted)',
                fontSize: '12px',
                fontWeight: '700',
                cursor: 'pointer'
              }}
            >
              Audit Log ({logs.length})
            </button>
          </div>

          <Button
            variant="primary"
            size="md"
            icon={<Plus size={16} />}
            onClick={() => setIsBuilderModalOpen(true)}
          >
            Create Rule
          </Button>
        </div>
      </div>

      {/* Main Content Area */}
      {activeTab === 'rules' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {rules.map((rule) => (
            <Card
              key={rule.id}
              padding="20px"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '16px',
                borderLeft: rule.enabled ? '4px solid var(--accent-teal)' : '4px solid var(--border-subtle)',
                opacity: rule.enabled ? 1 : 0.65
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1, minWidth: '280px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Zap size={18} color={rule.enabled ? 'var(--accent-teal)' : 'var(--text-muted)'} />
                  <h3 style={{ fontSize: '16px', fontWeight: '800', color: 'var(--text-primary)' }}>
                    {rule.name}
                  </h3>
                  <Badge variant={rule.enabled ? 'teal' : 'neutral'} size="xs">
                    {rule.enabled ? 'Active' : 'Disabled'}
                  </Badge>
                </div>

                {/* Condition blocks */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', fontSize: '13px' }}>
                  <span style={{ padding: '3px 8px', borderRadius: 'var(--radius-sm)', backgroundColor: 'var(--surface-recessed)', color: 'var(--text-secondary)' }}>
                    <strong>WHEN:</strong> {rule.trigger}
                  </span>
                  <span style={{ padding: '3px 8px', borderRadius: 'var(--radius-sm)', backgroundColor: 'var(--surface-recessed)', color: 'var(--text-secondary)' }}>
                    <strong>IF:</strong> {rule.condition}
                  </span>
                  <span style={{ padding: '3px 8px', borderRadius: 'var(--radius-sm)', backgroundColor: 'var(--surface-recessed)', color: 'var(--accent-teal)', fontWeight: '600' }}>
                    <strong>THEN:</strong> {rule.action}
                  </span>
                </div>

                <div style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'flex', gap: '12px', marginTop: '2px' }}>
                  <span>Executed <strong>{rule.executionCount}</strong> times</span>
                  <span>•</span>
                  <span>Last triggered: <strong>{rule.lastRun}</strong></span>
                </div>
              </div>

              {/* Action Controls */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Button
                  variant="ghost"
                  size="sm"
                  icon={<Play size={13} />}
                  onClick={() => handleTestRunRule(rule)}
                >
                  Test Run
                </Button>

                <button
                  onClick={() => handleToggleRule(rule.id)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: 'var(--radius-sm)',
                    backgroundColor: rule.enabled ? 'var(--surface-recessed)' : 'var(--accent-teal)',
                    color: rule.enabled ? 'var(--text-secondary)' : '#000',
                    border: 'none',
                    fontSize: '12px',
                    fontWeight: '700',
                    cursor: 'pointer'
                  }}
                >
                  {rule.enabled ? 'Disable' : 'Enable'}
                </button>

                <button
                  onClick={() => handleDeleteRule(rule.id)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--text-muted)',
                    cursor: 'pointer',
                    padding: '6px'
                  }}
                  title="Delete automation"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card padding="20px">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <History size={16} color="var(--accent-teal)" />
            <h3 style={{ fontSize: '15px', fontWeight: '800' }}>Automation Execution Logs</h3>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {logs.map((log) => (
              <div
                key={log.id}
                style={{
                  padding: '12px 14px',
                  borderRadius: 'var(--radius-md)',
                  backgroundColor: 'var(--surface-recessed)',
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: '12px'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  <CheckCircle2 size={16} color="var(--color-success)" style={{ marginTop: '2px' }} />
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)' }}>
                      {log.ruleName}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                      {log.details}
                    </div>
                  </div>
                </div>

                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <Badge variant="success" size="xs">{log.result}</Badge>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>{log.time}</div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* CREATE AUTOMATION MODAL */}
      <Modal
        isOpen={isBuilderModalOpen}
        onClose={() => setIsBuilderModalOpen(false)}
        title="Create Automation Rule"
        subtitle="Define triggers, conditions, and actions for automated execution"
        footer={
          <>
            <Button variant="ghost" onClick={() => setIsBuilderModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleCreateRuleSubmit} icon={<Sparkles size={15} />}>
              Save & Activate Rule
            </Button>
          </>
        }
      >
        <form onSubmit={handleCreateRuleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Input
            label="Rule Name *"
            placeholder="e.g. Notify Lead on Urgent Task, Auto-Move Completed to Sprint Velocity"
            value={ruleName}
            onChange={(e) => setRuleName(e.target.value)}
            required
            autoFocus
          />

          <Select
            label="1. WHEN (Trigger Event) *"
            options={triggerOptions}
            value={selectedTrigger}
            onChange={setSelectedTrigger}
          />

          <Select
            label="2. IF (Condition Filter)"
            options={conditionOptions}
            value={selectedCondition}
            onChange={setSelectedCondition}
          />

          <Select
            label="3. THEN (Action Execution) *"
            options={actionOptions}
            value={selectedAction}
            onChange={setSelectedAction}
          />
        </form>
      </Modal>
    </div>
  );
};
